"use client";

import { Citrus, FlaskConical, Barrel, Wine } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "CONSULT",
  Icon: Citrus,
  description: "We begin by understanding your unique requirements. Our experts analyze your project scope to ensure every detail aligns with your vision."
}, {
  number: "02",
  title: "DESIGN",
  Icon: FlaskConical,
  description: "Our architects draft precise blueprints. We iterate on structural integrity and aesthetic appeal to create a foundation built for longevity."
}, {
  number: "03",
  title: "EXECUTE",
  Icon: Barrel,
  description: "Skilled craftsmen bring the design to life. We utilize premium materials and rigorous standards to ensure flawless implementation."
}, {
  number: "04",
  title: "DELIVER",
  Icon: Wine,
  description: "Final inspection and handover. We ensure every component meets our quality benchmark before your project is officially commissioned."
}];
export default function HowToOrder() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="how-to-order" className="bg-background text-foreground relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-sans text-[10px] font-medium tracking-[0.3em] uppercase text-foreground/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; THE PROCESS
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[var(--chart-1)]" />
              <span className="font-sans text-xs font-medium tracking-[0.25em] uppercase text-[var(--chart-1)]">
                Ordering Protocol
              </span>
            </div>
            <h2 className="font-default italic text-4xl leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-[64px]">
              Precision engineering
              <br />
              <span className="text-foreground/70">
                starts with a conversation.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-foreground/75 md:text-xl">
              At our firm, every project begins with clarity. We manage workflows 
              in controlled phases, ensuring that every technical specification 
              and design requirement is met with absolute accuracy.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-foreground/60">
              No shortcuts. No ambiguity. Only the systematic, professional 
              execution of your project goals.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-[var(--chart-1)]" />
                        <span className="h-px w-1 bg-[var(--chart-1)]/60" />
                        <span className="h-px w-0.5 bg-[var(--chart-1)]/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-white/10 bg-card backdrop-blur-xl shadow-lg transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[var(--chart-1)] group-hover:shadow-xl">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-sans text-[120px] font-extrabold leading-none tracking-tighter text-foreground/10 transition-colors duration-[400ms] group-hover:text-[var(--chart-1)]" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-foreground/80 transition-colors duration-300 group-hover:text-[var(--chart-1)]" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-foreground/30 transition-all duration-500 group-hover:w-16 group-hover:bg-[var(--chart-1)]" />
                        <h3 className="font-default font-sans text-2xl font-bold tracking-[0.15em] uppercase text-foreground">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-foreground/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--chart-1)] to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 sm:flex-row sm:items-center">
            <p className="font-sans text-xs tracking-[0.25em] uppercase text-foreground/50">
              Four phases &mdash; one standard
            </p>
            <p className="font-default italic text-sm text-foreground/60">
              Quality assured in every delivery.
            </p>
          </div>
        </div>
      </div>
    </section>;
}